import { IconType } from 'react-icons';
import {
    TbBuilding,
    TbCalendarSearch,
    TbFileText,
    TbLayoutGrid,
    TbNews,
    TbSearch,
    TbTransactionDollar,
    TbUserSearch,
    TbWorldDollar,
} from 'react-icons/tb';

import { isSearchDomainAvailable } from '@/shared/constants';
import type { SearchPreferenceTabKey } from '@/types';

import { ResolvedSearchPreferences } from './preferences';

export const SEARCH_HORIZONTAL_GUTTER = '0.75rem';

const allSearchDomains = [
    'workspace',
    'companies',
    'people',
    'documents',
    'economic',
    'trade',
    'news',
    'events',
] as const;
export type AllSearchDomain = (typeof allSearchDomains)[number];
export const ALL_SEARCH_DOMAINS: readonly AllSearchDomain[] = allSearchDomains.filter(isSearchDomainAvailable);
export const DOCUMENT_SEARCH_SOURCE_KEYS = new Set([
    'box',
    'coda',
    'confluence',
    'docusign',
    'dropbox',
    'googleDrive',
    'googleSheets',
    'miro',
    'notion',
    'slack',
    'smartsheet',
]);
export const ECONOMIC_SEARCH_CATALOG = [
    { frequency: 'quarterly', category: 'macro', id: 'GDP', title: 'Gross Domestic Product (GDP)' },
    { frequency: 'quarterly', category: 'macro', id: 'realGDP', title: 'Real GDP' },
    { category: 'macro', id: 'CPI', title: 'Consumer Price Index (CPI)' },
    { category: 'macro', id: 'inflationRate', title: 'Inflation Rate' },
    { category: 'macro', id: 'federalFunds', title: 'Federal Funds Rate' },
    { category: 'macro', id: 'unemploymentRate', title: 'Unemployment Rate' },
    { category: 'macro', id: 'consumerSentiment', title: 'Consumer Sentiment' },
    { category: 'macro', id: 'smoothedUSRecessionProbabilities', title: 'US Recession Probability' },
    { category: 'treasury', id: 'month1', title: 'US 1 Month Treasury' },
    { category: 'treasury', id: 'month3', title: 'US 3 Month Treasury' },
    { category: 'treasury', id: 'year1', title: 'US 1 Year Treasury' },
    { category: 'treasury', id: 'year2', title: 'US 2 Year Treasury' },
    { category: 'treasury', id: 'year5', title: 'US 5 Year Treasury' },
    { category: 'treasury', id: 'year10', title: 'US 10 Year Treasury' },
    { category: 'treasury', id: 'year30', title: 'US 30 Year Treasury' },
    { category: 'market', id: 'market-risk-premium', title: 'Market Risk Premiums' },
    { category: 'market', id: 'commodities', title: 'Commodities' },
    { category: 'market', id: 'forex', title: 'Foreign Exchange' },
    { category: 'market', id: 'crypto', title: 'Cryptocurrency Markets' },
] as const;
export interface UnifiedSearchResultMetadata {
    label: string;
    value: string;
}

export interface UnifiedSearchResult {
    description?: string;
    domain: AllSearchDomain;
    exchange?: string;
    external?: boolean;
    id: string;
    kind: string;
    logoUrl?: string;
    metadata: UnifiedSearchResultMetadata[];
    source: string;
    symbol?: string;
    title: string;
    updatedAt?: string;
    url?: string;
    website?: string;
}

export type SearchTabKey = SearchPreferenceTabKey;

const SEARCH_TAB_DIVIDER_BOUNDARIES: readonly SearchTabKey[] = ['all', 'trade'];

export const hasSearchTabDividerAfter = (tabKey: SearchTabKey): boolean =>
    SEARCH_TAB_DIVIDER_BOUNDARIES.includes(tabKey);

export interface SearchTabDefinition {
    description: string;
    Icon: IconType;
    key: SearchTabKey;
    label: string;
}

const allSearchTabDefinitions: SearchTabDefinition[] = [
    {
        description: 'Search every supported workspace, market, document, people, and event source.',
        Icon: TbSearch,
        key: 'all',
        label: 'All',
    },
    {
        description: 'Search workspace resources, navigation, actions, and connected context.',
        Icon: TbLayoutGrid,
        key: 'workspace',
        label: 'Workspace',
    },
    {
        description: 'Search public and imported company and fund entities.',
        Icon: TbBuilding,
        key: 'companies',
        label: 'Companies',
    },
    {
        description: 'Find people using identity, role, education, and keyword filters.',
        Icon: TbUserSearch,
        key: 'people',
        label: 'People',
    },
    {
        description: 'Search workspace and connected documents.',
        Icon: TbFileText,
        key: 'documents',
        label: 'Documents',
    },
    {
        description: 'Explore economic series and macroeconomic indicators.',
        Icon: TbTransactionDollar,
        key: 'economic',
        label: 'Economics',
    },
    {
        description: 'Explore global trade corridors, hubs, sources, and geographic context.',
        Icon: TbWorldDollar,
        key: 'trade',
        label: 'Global Trade',
    },
    {
        description: 'Monitor market, company, fundraising, transaction, IPO, and M&A coverage.',
        Icon: TbNews,
        key: 'news',
        label: 'News',
    },
    {
        description: 'Explore dividend dates and macroeconomic releases in the market calendar.',
        Icon: TbCalendarSearch,
        key: 'events',
        label: 'Events',
    },
];

export const searchTabDefinitions = allSearchTabDefinitions.filter((tab) => isSearchDomainAvailable(tab.key));

export const isSearchTabEnabled = (tab: SearchTabDefinition, preferences: ResolvedSearchPreferences['tabs']) =>
    preferences[tab.key];
